// src/App.js
import { useEffect, useState } from 'react'
import { Routes, Route, Link, useNavigate } from 'react-router-dom'
import Home from './pages/Home'
import Catalog from './pages/Catalog'
import ProductDetails from './pages/ProductDetails'
import Cart from './pages/Cart'
import CheckoutGuest from './pages/CheckoutGuest'
import TrackOrder from './pages/TrackOrder'
import CategoriesBar from './components/CategoriesBar'
import CategoryPage from './pages/CategoryPage'
import CorporateHome from "./pages/CorporateHome"
import MarketplaceHomePage from './pages/MarketplaceHomePage'
import PaymentReturn from './pages/PaymentReturn'
import Footer from './components/layout/Footer'
import Login from './pages/auth/Login'
import RequireRole from './components/RequireRole'
import VendorProductsManager from './pages/vendor/ProductsManager'
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrdersReview from './pages/admin/OrdersReview'
import AdminUsers from './pages/admin/Users'
import SponsoredShelves from './pages/admin/SponsoredShelves'
import AdminAffiliatePayouts from './pages/admin/AdminAffiliatePayouts'
import HeroGridsAdmin from './pages/admin/HeroGrids'
import AdminCategories from './pages/admin/AdminCategories'
import HeroBannersAdmin from './pages/admin/HeroBanners'
import DealsSliderAdmin from './pages/admin/DealsSliderAdmin'
import FeaturedVideosAdmin from './pages/admin/FeaturedVideosAdmin'
import DealsShelvesAdmin from './pages/admin/DealsShelvesAdmin'
import InscriptionVendeur from './pages/InscriptionVendeur'
import { UiFeedbackProvider } from './context/UiFeedbackContext'
import { watchAuth, logout } from './lib/auth'
import { getCart, subscribeCart } from './lib/cartStore'
import AdminVendorRequests from './pages/admin/AdminVendorRequests'
import AdminVendorProductsValidation from './pages/admin/AdminVendorProductsValidation'
import AdminVendorProductsWrapper from "./pages/admin/AdminVendorProductsWrapper"
import UpdateDollarStoreToPending from "./pages/admin/UpdateDollarStoreToPending"
import PaymentReturnSubscription from './pages/PaymentReturnSubscription'
import ProtectedVendorRoute from "./components/ProtectedVendorRoute"
import VendorDashboard from "./pages/vendor/VendorDashboard"
import VendorSubscription from './pages/vendor/VendorSubscription'
import VendorProducts from "./pages/vendor/VendorProducts"
import AdminOrders from './pages/admin/AdminOrders'
import VendorOrders from './pages/vendor/VendorOrders'
import PrivacyPolicy from "./pages/PrivacyPolicy"
import TermsOfUse from "./pages/TermsOfUse"
import CommunityGuidelines from "./pages/CommunityGuidelines"
import ChangePassword from "./pages/vendor/ChangePassword"
import VendorProfileEdit from "./pages/VendorProfileEdit"
import ScrollToTop from './components/ScrollToTop'
import VendorSignupSuccess from "./pages/VendorSignupSuccess";
import VendorOnboarding from "./pages/vendor/VendorOnboarding";
import VendorSetPassword from "./pages/vendor/VendorSetPassword";
import VendorPayPack from './pages/vendor/VendorPayPack'
import AdminVendorPayments from './pages/admin/AdminVendorPayments'
import VendorSponsoredDeals from './pages/vendor/VendorSponsoredDeals'
import AdminSponsoredDeals from './pages/admin/AdminSponsoredDeals'
import ShopPage from "./pages/ShopPage";
import ContactSupport from "./pages/ContactSupport";
import DownloadApp from "./pages/DownloadApp";
import ShareRedirectPage from "./pages/ShareRedirectPage";

export default function App() {
  const [authState, setAuthState] = useState(null)
  const [cartCount, setCartCount] = useState(0)
  const [navSearch, setNavSearch] = useState('')
  const navigate = useNavigate()

  // Auth
  useEffect(() => {
    const unsub = watchAuth(setAuthState)
    return () => {
      if (typeof unsub === 'function') unsub()
    }
  }, [])

  const isAdmin = !!authState?.claims?.admin

  // Compteur panier
  useEffect(() => {
    const syncCartCount = (cartLines) => {
      const lines = cartLines || getCart() || []
      const total = lines.reduce((sum, l) => sum + (l.qty || 0), 0)
      setCartCount(total)
    }

    syncCartCount()
    const unsubscribe = subscribeCart(syncCartCount)

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe()
    }
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (e) {
      console.error(e)
      alert("Erreur lors de la déconnexion.")
    }
  }

  // Recherche navbar
  const handleNavSearchSubmit = (e) => {
    e.preventDefault()
    const q = navSearch.trim()
    if (!q) return
    navigate(`/catalog?q=${encodeURIComponent(q)}`)
  }

  const vendorId =
    localStorage.getItem("vendorId") ||
    localStorage.getItem("currentVendorId") ||
    ""

  return (
    <UiFeedbackProvider>
      <ScrollToTop />

      <div className="app-shell">
        {/* NAVBAR */}
        <nav className="navbar navbar-expand-lg navbar-dark navbar-custom sticky-top">
          <div className="container">

            {/* Ligne MOBILE : logo + search + panier + burger */}
            <div className="d-flex align-items-center w-100 gap-2 d-lg-none">
              <Link
                className="navbar-brand d-flex align-items-center gap-2 mb-0"
                to="/"
              >
                <img src="/logo.png" alt="GoShop" height="26" />
              </Link>

              <form
                className="flex-grow-1"
                onSubmit={handleNavSearchSubmit}
              >
                <div className="input-group input-group-sm">
                  <input
                    type="search"
                    className="form-control border-2 border-primary"
                    placeholder="Rechercher…"
                    value={navSearch}
                    onChange={(e) => setNavSearch(e.target.value)}
                    style={{
                      borderRadius: '999px',
                      fontSize: '0.9rem',
                    }}
                  />
                  <span className="input-group-text bg-white border-end-0">
                    <i className="bi bi-search text-muted" />
                  </span>
                </div>
              </form>

              <Link
                className="btn btn-outline-success btn-sm position-relative d-flex align-items-center justify-content-center"
                to="/cart"
                aria-label="Voir le panier"
              >
                <i className="bi bi-cart3 fs-5" />
                {cartCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {cartCount}
                  </span>
                )}
              </Link>

              <button
                className="navbar-toggler ms-1"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#mainnav"
                aria-controls="mainnav"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>

            {/* Desktop + menu déroulant mobile */}
            <div className="collapse navbar-collapse mt-2 mt-lg-0" id="mainnav">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center w-100 gap-2">
                <Link
                  className="navbar-brand d-none d-lg-flex align-items-center gap-2 me-lg-3"
                  to="/"
                >
                  <img src="/logo.png" alt="GoShop" height="28" />
                </Link>

                <ul className="navbar-nav mb-2 mb-lg-0 me-lg-3">
                  <li className="nav-item">
                    <Link className="nav-link" to="/catalog">
                      Catalogue
                    </Link>
                  </li>

                  {isAdmin && (
                    <>
                      <li className="nav-item">
                        <Link className="nav-link" to="/admin/dashboard">
                          Dashboard
                        </Link>
                      </li>
                    </>
                  )}
                  {authState?.claims?.vendor && (
                    <li className="nav-item">
                      <Link className="nav-link" to="/vendor/dashboard">
                        Espace vendeur
                      </Link>
                    </li>
                  )}
                </ul>

                {/* Search desktop */}
                <form
                  className="d-none d-lg-flex flex-grow-1 my-2 my-lg-0"
                  onSubmit={handleNavSearchSubmit}
                >
                  <div className="input-group input-group-sm w-100">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted" />
                    </span>
                    <input
                      type="search"
                      className="form-control border-start-0"
                      placeholder="Rechercher un produit, marque, catégorie..."
                      value={navSearch}
                      onChange={(e) => setNavSearch(e.target.value)}
                    />
                  </div>
                </form>

                {/* Zone droite */}
                <div className="d-flex flex-column flex-lg-row gap-2 align-items-lg-center ms-lg-auto my-2 my-lg-0">
                  {authState?.user && (
                    <span className="small text-muted d-none d-lg-inline">
                      {authState.user.email}
                    </span>
                  )}

                  <Link
                    className="btn btn-outline-success btn-sm position-relative d-none d-lg-flex align-items-center justify-content-center"
                    to="/cart"
                    aria-label="Voir le panier"
                  >
                    <i className="bi bi-cart3 fs-5" />
                    {cartCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {cartCount}
                      </span>
                    )}
                  </Link>

                  {authState?.user ? (
                    <button
                      className="btn btn-soft btn-sm"
                      type="button"
                      onClick={handleLogout}
                    >
                      Se déconnecter
                    </button>
                  ) : (
                    <Link className="btn btn-soft btn-sm" to="/login">
                      Se connecter
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Barre catégories */}
        <CategoriesBar />

        {/* MAIN + routes */}
        <main className="app-main">
          <div className="container py-4">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop/:vendorId" element={<ShopPage />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/c/:catId/:slug?" element={<CategoryPage />} />
              <Route path="/p/:pid" element={<ProductDetails />} />
              <Route path="/ip/:slug/:pid" element={<ProductDetails />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<CheckoutGuest />} />
              <Route path="/track/:oid" element={<TrackOrder />} />
              <Route path="/payment-return" element={<PaymentReturn />} />
              <Route path="/corporate" element={<CorporateHome />} />
              <Route path="/become-vendor" element={<MarketplaceHomePage />} />
              <Route path="/inscription-vendeur" element={<InscriptionVendeur />} />
              <Route path="/inscription-vendeur/success" element={<VendorSignupSuccess />} />
              <Route path="/login" element={<Login />} />
              <Route path="/payment-return-subscription" element={<PaymentReturnSubscription />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/community" element={<CommunityGuidelines />} />
              <Route path="/contact" element={<ContactSupport />} />
              <Route path="/download" element={<DownloadApp />} />
              <Route path="/vendor/change-password" element={<ChangePassword />} />
              <Route path="/vendor/profil" element={<VendorProfileEdit />} />
              <Route path="/admin/update-pending" element={<UpdateDollarStoreToPending />} />
              <Route path="/l/:code" element={<ShareRedirectPage />} />
              <Route
                path="/admin/dashboard"
                element={
                  <RequireRole allow={['admin']}>
                    <AdminDashboard />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/vendor-payments"
                element={
                  <RequireRole allow={['admin']}>
                    <AdminVendorPayments />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/affiliate-payouts"
                element={
                  <RequireRole allow={['admin']}>
                    <AdminAffiliatePayouts />
                  </RequireRole>
                } />
              <Route
                path="/vendor/sponsored"
                element={
                  <ProtectedVendorRoute>
                    <VendorSponsoredDeals />
                  </ProtectedVendorRoute>
                }
              />

              <Route
                path="/admin/sponsored-deals"
                element={
                  <RequireRole allow={["admin"]}>
                    <AdminSponsoredDeals />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireRole allow={['admin']}>
                    <AdminUsers />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/sponsored"
                element={
                  <RequireRole allow={['admin']}>
                    <SponsoredShelves />
                  </RequireRole>
                }
              />
              

              <Route
                path="/admin/herogrids"
                element={
                  <RequireRole allow={['admin']}>
                    <HeroGridsAdmin />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/herobanners"
                element={
                  <RequireRole allow={['admin']}>
                    <HeroBannersAdmin />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/deals-slider"
                element={
                  <RequireRole allow={['admin']}>
                    <DealsSliderAdmin />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/featured-videos"
                element={
                  <RequireRole allow={['admin']}>
                    <FeaturedVideosAdmin />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/categories"
                element={
                  <RequireRole allow={['admin']}>
                    <AdminCategories />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/deals-shelves"
                element={
                  <RequireRole allow={['admin']}>
                    <DealsShelvesAdmin />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/vendor-requests"
                element={
                  <RequireRole allow={['admin']}>
                    <AdminVendorRequests />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/vendor-products-validation"
                element={
                  <RequireRole>
                    <AdminVendorProductsValidation />
                  </RequireRole>
                }
              />

              <Route
                path="/vendor/dashboard"
                element={
                  <ProtectedVendorRoute>
                    <VendorDashboard />
                  </ProtectedVendorRoute>
                }
              />

              <Route path="/vendor/abonnement" element={<VendorSubscription />} />

              <Route
                path="/vendor/products"
                element={
                  <ProtectedVendorRoute>
                    <VendorProducts />
                  </ProtectedVendorRoute>
                }
              />

              <Route
                path="/admin/vendor-products"
                element={
                  <RequireRole>
                    <VendorProducts adminMode={true} />
                  </RequireRole>
                }
              />

              <Route
                path="/admin/vendor/:vendorId/products"
                element={
                  <RequireRole>
                    <AdminVendorProductsWrapper />
                  </RequireRole>
                }
              />

              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/vendor/orders" element={<VendorOrders vendorId={vendorId} />} />
              <Route path="/vendor/onboarding" element={<VendorOnboarding />} />
              <Route path="/vendor/set-password" element={<VendorSetPassword />} />
              <Route path="/vendor/pay-pack" element={<VendorPayPack />} />
            </Routes>
          </div>
        </main>

        {/* FOOTER global */}
        <Footer />
      </div>
    </UiFeedbackProvider>
  )
}